import classNames from "classnames";
import React, {useCallback, useContext, useEffect, useState} from "react";
import PaymentElementStripe from "../payment-element-stripe/PaymentElementStripeController.jsx";
import PaymentInHouse from "../payment-in-house/PaymentInHouse";
import {GlobalContext} from "../../globals/globalContext";
import {get} from "../../util/fetch";
import {FormattedMessage, useIntl} from "react-intl";


export default function Payment(props) {
  const {
    className,
    onSaveClick,
    slim,
    hasBeenClicked,
    setHasBeenClicked,
    addInvoicePaymentMethod,
    addSepaPaymentMethod,
    paymentProvider,
    isFromRegistrationForm,
    errors,
    clearErrors,
  } = props;

  const {
    subscription,
    creditorIdentifier,
    sepaCsvEmail,
    companyEmail,
    companyAddress,
    gmbhName,
  } = useContext(GlobalContext);

  const customer = subscription.customer;

  const intl = useIntl();
  const paymentsMap = {
    card: intl.formatMessage({ id: "payment_card" }),
    sepa_debit: intl.formatMessage({ id: "payment_sepa" }),
    paypal: "Paypal",
    customer_balance: intl.formatMessage({ id: "payment_invoice" }),
    invoice: intl.formatMessage({ id: "payment_invoice" }),
    sepa: intl.formatMessage({ id: "payment_sepa" }),
  };

  const isSettings = slim;

  const [currentPaymentData, setCurrentPaymentData] = useState(undefined);
  const [paymentType, setPaymentType] = useState(null);
  const [paymentMethodsOrSources, setPaymentMethodsOrSources] = useState([]);

  const getCurrentPaymentData = useCallback((customerId) => {
    let _url = `${location.origin}/api/customer/${customerId}/stripe_payment_method`;
    get(_url)
      .then((resp) => {
        setCurrentPaymentData(resp);
        setPaymentType(resp.payment_method?.type || resp.payment_source?.type);
        if (resp.stripe_payment_methods.length) {
          setPaymentMethodsOrSources(resp.stripe_payment_methods);
        } else if (resp.stripe_sources.length) {
          setPaymentMethodsOrSources(resp.stripe_sources);
        }
      })
      .catch((error) => {
        console.error("error: ", error);
      });
  });

  const renderErrorMessage = () => {
    if (!errors || Object.keys(errors).length === 0) return null;

    return (
      <div className="payment-error-message">
        <p>
          <FormattedMessage
            id="paymentErrorMessage"
            values={{
              contactLink: (
                <a href={`mailto:${companyEmail}`}>
                  {intl.formatMessage({ id: "contactSupport" })}
                </a>
              )
            }}
          />
        </p>
      </div>
    );
  };

  useEffect(() => {
    if (isSettings && customer?.id) {
      getCurrentPaymentData(customer.id);
    }
  }, []);

  const renderStripePaymentMethodInfo = () => {
    return paymentType ? (
      <div className="payment-method">
        {paymentProvider === "in_house" && (
          <div className="transition-message">
            <p>
              <strong>
                <FormattedMessage id="systemChangePaymentMethod" />
              </strong>
            </p>
          </div>
        )}
        <p>
          <FormattedMessage id="currentPaymentMethod" />
        </p>
        <ul className="current-payment-info">
          <li>
            {paymentsMap[paymentType]}&nbsp;
            {paymentMethodsOrSources.length > 0 && (
              <>
                {paymentMethodsOrSources.map((source, i) => (
                  <span key={i}>
                    {source.type === "sepa_debit" && (
                      <>
                        ({source.sepa_debit.country}********************
                        {source.sepa_debit.last4})
                      </>
                    )}
                    {source.type === "card" && (
                      <>(************{source.card.last4})</>
                    )}
                    {source.type === "customer_balance" && (
                      <>{intl.formatMessage({ id: "payment_invoice" })}</>
                    )}
                  </span>
                ))}
              </>
            )}
          </li>
        </ul>
      </div>
    ) : null;
  };

  const renderInHousePaymentInfo = (paymentData) => {
    if (!paymentData) return null;

    const { method, iban, account_holder_name } = paymentData;

    const formatIban = (iban) => {
      if (!iban) return "N/A";
      const lastTwo = iban.slice(-2);
      const middleTwo = iban.slice(-4, -2);
      const firstFour = iban.slice(0, 4);
      return `${firstFour} **** **** **** **${middleTwo} ${lastTwo}`;
    };

    const maskedIban = formatIban(iban);

    return (
      <div className="payment-method">
        {paymentProvider === "stripe" && (
          <div className="transition-message">
            <p>
              <strong>
                <FormattedMessage id="systemChangePaymentMethod" />
              </strong>
            </p>
          </div>
        )}
        <p>
          <FormattedMessage id="currentPaymentMethod" />
        </p>
        <div className="current-payment-info">
          <div>
            <strong>{paymentsMap[method] || method}</strong>
          </div>
          {method === "sepa" && (
            <>
              <div>
                <strong>
                  <FormattedMessage id="accountHolder" />:{" "}
                </strong>
                {account_holder_name || "N/A"}
              </div>
              <div>
                <strong>IBAN: </strong>
                {maskedIban}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classNames("payment", { "is-slim": slim }, className)}>
      {renderErrorMessage()}
      {isSettings && paymentProvider && (
        <>
          {(() => {
            if (currentPaymentData) {
              if (
                (paymentMethodsOrSources &&
                  paymentMethodsOrSources.length > 0) ||
                paymentType === "customer_balance"
              ) {
                return renderStripePaymentMethodInfo();
              } else if (currentPaymentData.payment_in_house?.method) {
                return renderInHousePaymentInfo(
                  currentPaymentData.payment_in_house,
                );
              }
            }
            return null;
          })()}
          <p style={{ marginTop: "20px" }}>
            {" "}
            <FormattedMessage id="changePaymentMethod" />:
          </p>
        </>
      )}
      {paymentProvider === "stripe" && (
        <PaymentElementStripe
          customer={customer}
          hasBeenClicked={hasBeenClicked}
          setHasBeenClicked={setHasBeenClicked}
          onSetupIntentConfirmed={(paymentType, setupIntentId = undefined) =>
            onSaveClick("stripe", paymentType, setupIntentId)
          }
          addInvoicePaymentMethod={addInvoicePaymentMethod}
          isFromRegistrationForm={isFromRegistrationForm}
          clearErrors={clearErrors}
        />
      )}
      {paymentProvider === "in_house" && (
        <PaymentInHouse
          customer={customer}
          hasBeenClicked={hasBeenClicked}
          setHasBeenClicked={setHasBeenClicked}
          onSave={(paymentType, sepaData) =>
            onSaveClick("in_house", paymentType, null, sepaData)
          }
          addInvoicePaymentMethod={addInvoicePaymentMethod}
          addSepaPaymentMethod={addSepaPaymentMethod}
          creditorIdentifier={creditorIdentifier}
          sepaCsvEmail={sepaCsvEmail}
          companyAddress={companyAddress}
          gmbhName={gmbhName}
          isFromRegistrationForm={isFromRegistrationForm}
          clearErrors={clearErrors}
        />
      )}
    </div>
  );
}
